import { useState } from 'react';

import { useMutation } from '@apollo/client/react';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';

import type { GetUsersListQuery } from '@graphql/__generated__/graphql';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { UserSelect } from '@modules/_shared/components/user-select/UserSelect';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { emitFeedRefresh } from '@modules/_shared/utils/feed-refresh';
import { BULK_REASSIGN_USER_ACCOUNT_MANAGER } from '@modules/users/userQueries';
import { useLocale } from '@shared/hooks/locale';

type User = GetUsersListQuery['users'][number];

export const BulkAccountManagerReassignDialog = ({
  users,
  onClose,
  onReassigned,
}: {
  users: User[];
  onClose: () => void;
  onReassigned: (count: number) => void;
}) => {
  const { t } = useLocale();
  const { me } = useAppData();
  const [newAccountManagerId, setNewAccountManagerId] = useState<string | null>(
    null,
  );
  const [transferOpenActivities, setTransferOpenActivities] = useState(false);
  const [reassign, { loading, error, reset }] = useMutation(
    BULK_REASSIGN_USER_ACCOUNT_MANAGER,
  );
  const changedCount = users.filter(
    user => user.account_manager_id !== newAccountManagerId,
  ).length;

  const handleSubmit = async () => {
    if (newAccountManagerId == null || loading || changedCount === 0) {
      return;
    }
    const result = await reassign({
      variables: {
        users: users.map(({ id }) => ({ id })),
        newAccountManagerId,
        transferOpenActivities,
      },
    }).catch(() => null);
    if (result?.data != null) {
      emitFeedRefresh();
      onReassigned(result.data.bulk_reassign_user_account_manager.length);
    }
  };

  return (
    <>
      <Dialog
        open
        fullWidth
        maxWidth="sm"
        onClose={loading ? undefined : onClose}
      >
        <DialogTitle>{t('Reassign account manager')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <DialogContentText>
              {t('Reassign the account manager for {{count}} selected users.', {
                count: users.length,
              })}
            </DialogContentText>
            <Typography>{t('New account manager')}</Typography>
            <UserSelect
              userId={newAccountManagerId}
              onChange={setNewAccountManagerId}
              disabled={loading || me == null}
              filters={{
                tenant_id: { _eq: me?.tenant_id },
                is_admin: { _eq: true },
                is_deleted: { _eq: false },
              }}
              InputProps={{
                placeholder: t('selectAccountManager'),
                'aria-label': t('New account manager'),
                required: true,
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={transferOpenActivities}
                  disabled={loading}
                  onChange={(_, checked) => setTransferOpenActivities(checked)}
                />
              }
              label={t(
                'Also transfer unfinished activities from the current account manager',
              )}
            />
            <Typography variant="body2" color="text.secondary">
              {t(
                'Only open calls, meetings and tasks linked directly to these users and assigned to their current account manager will move. Completed activities, property work, calendar-linked activities and scheduled emails stay unchanged. Team and organisation assignments stay unchanged.',
              )}
            </Typography>
            {newAccountManagerId != null && changedCount === 0 && (
              <Alert severity="info">
                {t('These users already have this account manager.')}
              </Alert>
            )}
            {newAccountManagerId != null &&
              changedCount > 0 &&
              changedCount < users.length && (
                <Alert severity="info">
                  {t(
                    '{{count}} selected users already have this account manager and will stay unchanged.',
                    { count: users.length - changedCount },
                  )}
                </Alert>
              )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            loading={loading}
            disabled={newAccountManagerId == null || changedCount === 0}
            onClick={handleSubmit}
          >
            {t('Reassign account manager')}
          </Button>
        </DialogActions>
      </Dialog>
      <MutationErrorSnackbar error={error ?? null} onClose={reset} />
    </>
  );
};
